import React, { useContext, useEffect, useState } from "react";
import { SaasTherapistService } from "src/services/SaasTherapistService";
import { toast } from "react-toastify";
import UserContext from "src/context/UserContext";
import Spinner from "src/common/spinner/spinner";
import { UserDetails } from "src/models/User";
import { Modal } from "react-bootstrap";

interface JotformSaasProps {
  clientId: string;
}

interface JotformData {
  formName: string;
  resultLink: string;
  submissionDate: string;
  score_GAD_7: number;
  score_PHQ_9: number;
  score_AUDIT: number;
  score_DAST: number;
}

interface EnabledForm {
  _id: string;
  form_id: string;
  title_form: string;
  status: string;
}

const JotformSaas: React.FC<JotformSaasProps> = ({ clientId }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [jotformData, setJotformData] = useState<JotformData[]>([]);
  const [enabledForms, setEnabledForms] = useState<EnabledForm[]>([]);
  const [selectedFormId, setSelectedFormId] = useState<string>('');
  const [generatedLink, setGeneratedLink] = useState<string>('');
  const [user] = useContext(UserContext);

  // Schedule Form Modal States
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [scheduleOption, setScheduleOption] = useState<string>('');
  const [startDate, setStartDate] = useState<string>('');
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [selectedTime, setSelectedTime] = useState<string>('09:00');
  const [selectedDayOfWeek, setSelectedDayOfWeek] = useState<string>('');
  const [selectedDayOfMonth, setSelectedDayOfMonth] = useState<string>('');

  // Confirmation Modal States
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState<'cancel' | 'reset' | null>(null);

  // Next run time state
  const [nextRunMessage, setNextRunMessage] = useState<string>('');
  const [scheduleDetailsMessage, setScheduleDetailsMessage] = useState<string>('');
  const [hasExistingSchedule, setHasExistingSchedule] = useState<boolean>(false);
  const [currentScheduleId, setCurrentScheduleId] = useState<string>('');

  useEffect(() => {
    if (user?._id) {
      fetchJotformData();
      fetchEnabledForms();
    } else {
      toast.error("User information not available", {
        position: toast.POSITION.TOP_RIGHT,
        className: "foo-bar",
      });
      setIsLoading(false);
    }
  }, [clientId, user]);

  // Fetch next run time when form is selected
  useEffect(() => {
    if (selectedFormId) {
      fetchNextRunTime(selectedFormId);
    } else {
      setNextRunMessage('');
      setScheduleDetailsMessage('');
      setHasExistingSchedule(false);
      setCurrentScheduleId('');
    }
  }, [selectedFormId, user?._id, clientId]);

  const fetchJotformData = async () => {
    if (!user?._id) return;
    
    setIsLoading(true);
    try {
      const response = await SaasTherapistService.getJotFormByClientId(user._id, clientId);
      if (response.success) {
        setJotformData(response.data);
      } else {
        toast.error(response.error || "Error loading Jotform data", {
          position: toast.POSITION.TOP_RIGHT,
          className: "foo-bar",
        });
      }
    } catch (error) {
      toast.error("Failed to fetch Jotform data", {
        position: toast.POSITION.TOP_RIGHT,
        className: "foo-bar",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchEnabledForms = async () => {
    try {
      const response = await SaasTherapistService.getEnabledForms();
      if (response.success) {
        setEnabledForms(response.data);
      } else {
        toast.error(response.error || "Error loading enabled forms", {
          position: toast.POSITION.TOP_RIGHT,
          className: "foo-bar",
        });
      }
    } catch (error) {
      toast.error("Failed to fetch enabled forms", {
        position: toast.POSITION.TOP_RIGHT,
        className: "foo-bar",
      });
    }
  };

  const generateFormLink = () => {
    if (!selectedFormId || !user?._id) return;
    const link = `https://form.jotform.com/${selectedFormId}?hash_therapist=${user._id}&hash_clientid=${clientId}`;
    setGeneratedLink(link);
  };

  // Generate schedule details message based on form scheduling data
  const generateScheduleDetailsMessage = (formScheduling: any) => {
    const { scheduleOption, startDate, selectedDayOfWeek, selectedDayOfMonth, selectedTime } = formScheduling;

    const formatDate = (dateStr: string) => {
      return new Date(dateStr).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    };

    const formatTime = (timeStr: string) => {
      const [hours, minutes] = timeStr.split(':');
      const date = new Date();
      date.setHours(parseInt(hours), parseInt(minutes));
      return date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    };

    switch (scheduleOption) {
      case 'weekly':
        return `Weekly send from ${formatDate(startDate)}, every ${selectedDayOfWeek} at ${formatTime(selectedTime)}`;

      case 'biweekly':
        return `Biweekly send from ${formatDate(startDate)}, every other ${selectedDayOfWeek} at ${formatTime(selectedTime)}`;

      case 'monthly':
        return `Monthly send from ${formatDate(startDate)}, on day ${selectedDayOfMonth} of each month at ${formatTime(selectedTime)}`;

      case 'sixmonths':
        return `Every 6 months send from ${formatDate(startDate)} at ${formatTime(selectedTime)}`;

      default:
        return `Scheduled from ${formatDate(startDate)} at ${formatTime(selectedTime)}`;
    }
  };

  // Fetch next run time for selected form
  const fetchNextRunTime = async (formId: string) => {
    if (!formId || !user?._id) {
      setNextRunMessage('');
      setScheduleDetailsMessage('');
      setHasExistingSchedule(false);
      setCurrentScheduleId('');
      return;
    }

    try {
      const response = await SaasTherapistService.getNextRunOfJotform({
        clientId: clientId,
        therapistId: user._id,
        formId: formId
      });

      if (response.success && response.data?.nextRunTimes?.length > 0) {
        // Form has existing schedule
        setHasExistingSchedule(true);

        // Get the first (earliest) next run time
        const nextRun = response.data.nextRunTimes[0];
        const nextRunDate = new Date(nextRun.nextRunAt);

        // Format the date and time
        const formattedDate = nextRunDate.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        });

        const formattedTime = nextRunDate.toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit',
          hour12: true
        });

        setNextRunMessage(`This form is scheduled to be sent to the client on ${formattedDate} at ${formattedTime}.`);

        // Generate schedule details message if form scheduling data is available
        if (response.data?.formSchedulings?.length > 0) {
          const formScheduling = response.data.formSchedulings[0];
          const scheduleDetails = generateScheduleDetailsMessage(formScheduling);
          setScheduleDetailsMessage(scheduleDetails);
          setCurrentScheduleId(formScheduling._id);
        } else {
          setScheduleDetailsMessage('');
          setCurrentScheduleId('');
        }
      } else {
        // No existing schedule
        setHasExistingSchedule(false);
        setNextRunMessage('This form is not currently scheduled for this client.');
        setScheduleDetailsMessage('');
        setCurrentScheduleId('');
      }
    } catch (error) {
      console.error('Error fetching next run time:', error);
      setHasExistingSchedule(false);
      setNextRunMessage('This form is not currently scheduled for this client.');
      setScheduleDetailsMessage('');
      setCurrentScheduleId('');
    }
  };

  // Schedule Form Functions
  const handleScheduleFormClick = () => {
    if (!selectedFormId) {
      toast.error("Please select a form first", {
        position: toast.POSITION.TOP_RIGHT,
        className: "foo-bar",
      });
      return;
    }
    setShowScheduleModal(true);
  };

  const handleScheduleFormSubmit = async () => {
    // Get selected form name
    const selectedForm = enabledForms.find(form => form.form_id === selectedFormId);
    const formName = selectedForm?.title_form || 'Selected Form';

    // Prepare schedule data
    const scheduleData = {
      formId: selectedFormId,
      formName: formName,
      clientId: clientId,
      therapistId: user?._id || '',
      scheduleOption: scheduleOption,
      startDate: startDate,
      selectedDate: selectedDate,
      selectedTime: selectedTime,
      selectedDayOfWeek: selectedDayOfWeek,
      selectedDayOfMonth: selectedDayOfMonth
    };

    // Console log for debugging
    console.log('Schedule Form Data:', scheduleData);

    try {
      // Call API to schedule form
      const response = await SaasTherapistService.scheduleForm(scheduleData);

      if (response.success) {
        toast.success("Form schedule saved successfully!", {
          position: toast.POSITION.TOP_RIGHT,
          className: "foo-bar",
        });

        // Refresh next run time message
        fetchNextRunTime(selectedFormId);

        // Close modal and reset form
        setShowScheduleModal(false);
        resetScheduleForm();
      } else {
        toast.error(response.error || "Failed to schedule form", {
          position: toast.POSITION.TOP_RIGHT,
          className: "foo-bar",
        });
      }
    } catch (error) {
      console.error('Error scheduling form:', error);
      toast.error("Failed to schedule form. Please try again.", {
        position: toast.POSITION.TOP_RIGHT,
        className: "foo-bar",
      });
    }
  };

  const resetScheduleForm = () => {
    setScheduleOption('');
    setStartDate('');
    setSelectedDate('');
    setSelectedTime('09:00');
    setSelectedDayOfWeek('');
    setSelectedDayOfMonth('');
  };

  // Show confirmation modal for cancel
  const handleCancelScheduleClick = () => {
    setConfirmAction('cancel');
    setShowConfirmModal(true);
  };

  // Show confirmation modal for reset
  const handleResetScheduleClick = () => {
    setConfirmAction('reset');
    setShowConfirmModal(true);
  };

  // Confirm action (cancel or reset)
  const handleConfirmAction = async () => {
    setShowConfirmModal(false);

    if (confirmAction === 'cancel') {
      await performCancelSchedule();
    } else if (confirmAction === 'reset') {
      await performResetSchedule();
    }

    setConfirmAction(null);
  };

  // Cancel Form Schedule (actual implementation)
  const performCancelSchedule = async () => {
    if (!currentScheduleId) {
      toast.error("No schedule found to cancel", {
        position: toast.POSITION.TOP_RIGHT,
        className: "foo-bar",
      });
      return;
    }

    try {
      const response = await SaasTherapistService.cancelFormSchedule(currentScheduleId);

      if (response.success) {
        toast.success("Form schedule cancelled successfully!", {
          position: toast.POSITION.TOP_RIGHT,
          className: "foo-bar",
        });

        // Refresh the next run time message
        if (selectedFormId) {
          fetchNextRunTime(selectedFormId);
        }
      } else {
        toast.error(response.error || "Failed to cancel form schedule", {
          position: toast.POSITION.TOP_RIGHT,
          className: "foo-bar",
        });
      }
    } catch (error) {
      console.error('Error cancelling form schedule:', error);
      toast.error("Failed to cancel form schedule. Please try again.", {
        position: toast.POSITION.TOP_RIGHT,
        className: "foo-bar",
      });
    }
  };

  // Reset Form Schedule (Cancel + Show Schedule Modal) - actual implementation
  const performResetSchedule = async () => {
    if (!currentScheduleId) {
      toast.error("No schedule found to reset", {
        position: toast.POSITION.TOP_RIGHT,
        className: "foo-bar",
      });
      return;
    }

    try {
      const response = await SaasTherapistService.cancelFormSchedule(currentScheduleId);

      if (response.success) {
        toast.success("Form schedule cancelled successfully!", {
          position: toast.POSITION.TOP_RIGHT,
          className: "foo-bar",
        });

        // Refresh the next run time message
        if (selectedFormId) {
          fetchNextRunTime(selectedFormId);
        }

        // Show schedule modal to create new schedule
        setShowScheduleModal(true);
      } else {
        toast.error(response.error || "Failed to cancel form schedule", {
          position: toast.POSITION.TOP_RIGHT,
          className: "foo-bar",
        });
      }
    } catch (error) {
      console.error('Error cancelling form schedule:', error);
      toast.error("Failed to cancel form schedule. Please try again.", {
        position: toast.POSITION.TOP_RIGHT,
        className: "foo-bar",
      });
    }
  };

  const isScheduleFormValid = () => {
    if (!scheduleOption) return false;

    if (scheduleOption === 'specific') {
      return selectedDate && selectedTime;
    }

    // For recurring schedules
    if (!startDate || !selectedTime) return false;

    if (scheduleOption === 'weekly' || scheduleOption === 'biweekly') {
      return selectedDayOfWeek;
    }

    if (scheduleOption === 'monthly' || scheduleOption === 'sixmonths') {
      return selectedDayOfMonth;
    }

    return true;
  };

  const copyToClipboard = () => {
    if (generatedLink) {
      navigator.clipboard.writeText(generatedLink).then(() => {
        toast.success("Link copied to clipboard!", {
          position: toast.POSITION.TOP_RIGHT,
          className: "foo-bar",
        });
      });
    }
  };

  const sendEmailInvitation = async () => {
    if (!generatedLink || !clientId) return;
    
    try {
      setIsSendingEmail(true);
      const response = await SaasTherapistService.sendJotformInvitation({
        clientId,
        formLink: generatedLink
      });
      
      if (response.success) {
        toast.success("Form invitation sent successfully!", {
          position: toast.POSITION.TOP_RIGHT,
          className: "foo-bar",
        });
      } else {
        toast.error(response.error || "Failed to send form invitation", {
          position: toast.POSITION.TOP_RIGHT,
          className: "foo-bar",
        });
      }
    } catch (error) {
      toast.error("Failed to send form invitation", {
        position: toast.POSITION.TOP_RIGHT,
        className: "foo-bar",
      });
    } finally {
      setIsSendingEmail(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = date.toLocaleString('en-US', { month: 'short' }).toUpperCase();
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "200px" }}>
        <Spinner className="bouncer" />
      </div>
    );
  }
  
  // Helper function to give score colors based on value ranges
  const getScoreColor = (score: number, type: string) => {
    if (score === 0) return '#6c757d';
    
    if (type === 'PHQ_9') {
      if (score < 5) return '#28a745'; // Minimal
      if (score < 10) return '#ffc107'; // Mild
      if (score < 15) return '#fd7e14'; // Moderate
      if (score < 20) return '#dc3545'; // Moderately Severe
      return '#6f42c1'; // Severe
    }
    
    if (type === 'GAD_7') {
      if (score < 5) return '#28a745'; // Minimal
      if (score < 10) return '#ffc107'; // Mild
      if (score < 15) return '#fd7e14'; // Moderate
      return '#dc3545'; // Severe
    }
    
    if (type === 'AUDIT') {
      if (score < 8) return '#28a745'; // Low Risk
      if (score < 16) return '#ffc107'; // Medium Risk
      if (score < 20) return '#fd7e14'; // High Risk
      return '#dc3545'; // Very High Risk
    }
    
    if (type === 'DAST') {
      if (score < 3) return '#28a745'; // Low
      if (score < 6) return '#ffc107'; // Moderate
      return '#dc3545'; // Substantial
    }
    
    return '#6c757d'; // Default gray for unknown types
  }

  return (
    <div className="animate__animated animate__fadeIn">
      {/* Link Generation Section */}
      <div className="mb-5" style={{
        background: 'linear-gradient(145deg, #f8f9fa, #ffffff)',
        padding: '30px',
        borderRadius: '12px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
        border: '1px solid #e9ecef'
      }}>
        <h4 className="mb-4" style={{ 
          color: '#2c3e50', 
          fontWeight: 'bold',
          display: 'flex',
          alignItems: 'center',
          gap: '10px'
        }}>
          <span style={{
            background: '#ff6634',
            color: 'white',
            width: '36px',
            height: '36px',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginRight: '5px'
          }}>
            <i className="bi bi-link-45deg"></i>
          </span>
          Form Link Generator
        </h4>
        <div className="row g-3 align-items-end">
          <div className="col-md-8">
            <select
              className="form-select form-select-lg"
              value={selectedFormId}
              onChange={(e) => setSelectedFormId(e.target.value)}
              style={{ 
                height: '50px', 
                fontSize: '16px',
                boxShadow: 'none',
                border: '1px solid #ced4da',
                borderRadius: '8px',
                paddingLeft: '16px',
                transition: 'all 0.3s ease-in-out'
              }}
            >
              <option value="">Select a form...</option>
              {enabledForms.map((form) => (
                <option key={form._id} value={form.form_id}>
                  {form.title_form}
                </option>
              ))}
            </select>
          </div>
          <div className="col-md-4">
            <button
              className="btn btn-primary w-100"
              onClick={generateFormLink}
              disabled={!selectedFormId}
              style={{
                height: '50px',
                background: '#ff6634',
                border: 'none',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: '600',
                boxShadow: '0 4px 12px rgba(255, 102, 52, 0.25)',
                transition: 'all 0.3s ease'
              }}
            >
              <i className="bi bi-magic me-1"></i> Generate Link
            </button>
          </div>
        </div>

        {/* Next Run Time Message */}
        {nextRunMessage && (
          <div className="mt-3">
            <div className="alert alert-info" style={{
              backgroundColor: '#fff3cd',
              borderColor: '#ffeaa7',
              color: '#856404',
              borderRadius: '8px',
              border: '1px solid #ffeaa7',
              padding: '12px 16px'
            }}>
              <div className="d-flex align-items-center">
                <i className="bi bi-info-circle-fill me-2" style={{ color: '#ff6634' }}></i>
                <div className="flex-grow-1">
                  <div>{nextRunMessage}</div>
                  {scheduleDetailsMessage && (
                    <div className="mt-1" style={{ fontSize: '14px', fontStyle: 'italic' }}>
                      {scheduleDetailsMessage}
                    </div>
                  )}
                </div>
              </div>
              {hasExistingSchedule && currentScheduleId && (
                <div className="mt-2 d-flex gap-2 justify-content-center">
                  <button
                    className="btn btn-sm btn-outline-danger flex-fill"
                    onClick={handleCancelScheduleClick}
                    style={{ fontSize: '12px', maxWidth: '140px' }}
                  >
                    <i className="bi bi-x-circle me-1"></i>
                    Cancel Schedule
                  </button>
                  <button
                    className="btn btn-sm btn-outline-warning flex-fill"
                    onClick={handleResetScheduleClick}
                    style={{ fontSize: '12px', maxWidth: '140px' }}
                  >
                    <i className="bi bi-arrow-clockwise me-1"></i>
                    Reset Schedule
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {generatedLink && (
          <div className="mt-3">
            <div className="input-group">
              <input
                type="text"
                className="form-control form-control-lg"
                value={generatedLink}
                readOnly
                style={{ 
                  height: '50px', 
                  fontSize: '16px',
                  border: '1px solid #ced4da',
                  borderRadius: '8px 0 0 8px',
                  backgroundColor: '#f8f9fa',
                  paddingLeft: '16px',
                  color: '#495057',
                  fontFamily: 'monospace',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}
              />
              <button
                className="btn btn-outline-primary"
                onClick={copyToClipboard}
                style={{
                  height: '50px',
                  borderColor: '#ff6634',
                  color: '#ff6634',
                  fontSize: '16px',
                  fontWeight: '600',
                  transition: 'all 0.2s ease',
                  borderRadius: '0',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                <i className="bi bi-clipboard"></i> Copy
              </button>
              <button
                className="btn btn-primary"
                onClick={sendEmailInvitation}
                disabled={isSendingEmail}
                style={{
                  height: '50px',
                  background: '#2979ff',
                  border: 'none',
                  borderRadius: '0 8px 8px 0',
                  fontSize: '16px',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  boxShadow: '0 4px 12px rgba(41, 121, 255, 0.25)',
                  transition: 'all 0.3s ease'
                }}
              >
                {isSendingEmail ? (
                  <>
                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                    Sending...
                  </>
                ) : (
                  <>
                    <i className="bi bi-envelope"></i>
                    Send Email
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {/* Schedule Form Button */}
        <div className="mt-3 d-flex justify-content-end">
          <button
            className={`btn ${hasExistingSchedule ? 'btn-secondary' : 'btn-outline-warning'}`}
            onClick={handleScheduleFormClick}
            disabled={!selectedFormId || hasExistingSchedule}
            style={{
              borderColor: hasExistingSchedule ? '#6c757d' : '#ff6634',
              color: hasExistingSchedule ? '#6c757d' : '#ff6634',
              fontSize: '16px',
              fontWeight: '600',
              padding: '8px 20px',
              borderRadius: '8px',
              transition: 'all 0.3s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              opacity: hasExistingSchedule ? 0.6 : 1,
              cursor: hasExistingSchedule ? 'not-allowed' : 'pointer'
            }}
            title={hasExistingSchedule ? 'This form already has a schedule' : 'Schedule this form'}
          >
            <i className={`bi ${hasExistingSchedule ? 'bi-check-circle' : 'bi-clock'} me-1`}></i>
            {hasExistingSchedule ? 'Already Scheduled' : 'Schedule Form'}
          </button>
        </div>
      </div>

      {/* Visual divider with label */}
      <div className="d-flex align-items-center mb-4">
        <div style={{ flex: 1, height: '2px', background: 'linear-gradient(to right, transparent, #dee2e6)' }}></div>
        <div className="px-4" style={{ 
          color: '#495057', 
          fontWeight: 'bold',
          letterSpacing: '1px',
          fontSize: '14px',
          backgroundColor: '#f8f9fa',
          padding: '8px 16px',
          borderRadius: '20px',
          border: '1px solid #dee2e6'
        }}>FORM SUBMISSIONS</div>
        <div style={{ flex: 1, height: '2px', background: 'linear-gradient(to left, transparent, #dee2e6)' }}></div>
      </div>

      {/* Results Table Section */}
      <div className="table-responsive" style={{ 
        background: 'white',
        borderRadius: '12px',
        boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
        padding: '10px',
        border: '1px solid #e9ecef',
        overflow: 'hidden'
      }}>
        <table className="table table-hover mb-0" style={{
          borderCollapse: 'separate',
          borderSpacing: '0',
        }}>
          <thead>
            <tr style={{
              background: '#f8f9fa',
              borderBottom: '2px solid #dee2e6',
            }}>
              <th style={{ padding: '15px', fontSize: '14px', fontWeight: '600', color: '#495057' }}>Form Name</th>
              <th style={{ padding: '15px', fontSize: '14px', fontWeight: '600', color: '#495057' }}>Submission Date</th>
              <th style={{ padding: '15px', fontSize: '14px', fontWeight: '600', color: '#495057', textAlign: 'center' }}>Score PHQ 9</th>
              <th style={{ padding: '15px', fontSize: '14px', fontWeight: '600', color: '#495057', textAlign: 'center' }}>Score GAD 7</th>
              <th style={{ padding: '15px', fontSize: '14px', fontWeight: '600', color: '#495057', textAlign: 'center' }}>Score AUDIT</th>
              <th style={{ padding: '15px', fontSize: '14px', fontWeight: '600', color: '#495057', textAlign: 'center' }}>Score DAST</th>
            </tr>
          </thead>
          <tbody>
            {jotformData.length === 0 ? (
              <tr>
                <td colSpan={6} className="text-center">No forms available</td>
              </tr>
            ) : (
              jotformData.map((form, index) => (
                <tr key={index} style={{
                    transition: 'all 0.2s ease',
                    backgroundColor: index % 2 === 0 ? 'white' : '#fafafa'
                  }}>
                  <td style={{ padding: '15px', verticalAlign: 'middle', fontWeight: '500' }}>{form.formName}</td>
                  <td style={{ padding: '15px', verticalAlign: 'middle', color: '#6c757d' }}>{formatDate(form.submissionDate)}</td>
                  <td style={{ padding: '15px', verticalAlign: 'middle', textAlign: 'center' }}>
                    {form.score_PHQ_9 > 0 ? (
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '5px' }}>
                        <span style={{ 
                          fontWeight: '600', 
                          fontSize: '18px', 
                          color: getScoreColor(form.score_PHQ_9, 'PHQ_9')
                        }}>
                          {form.score_PHQ_9}
                        </span>
                        <span
                          className="view-btn"
                          onClick={() => window.open(form.resultLink, "_blank")}
                          style={{
                            color: '#ff6634',
                            cursor: 'pointer',
                            fontSize: '12px',
                            fontWeight: '600',
                            textDecoration: 'underline',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '3px'
                          }}
                        >
                          <i className="bi bi-eye-fill" style={{ fontSize: '10px' }}></i> View
                        </span>
                      </div>
                    ) : (
                      <span style={{ color: '#6c757d', fontSize: '16px' }}>0</span>
                    )}
                  </td>
                  <td style={{ padding: '15px', verticalAlign: 'middle', textAlign: 'center' }}>
                    {form.score_GAD_7 > 0 ? (
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '5px' }}>
                        <span style={{ 
                          fontWeight: '600', 
                          fontSize: '18px', 
                          color: getScoreColor(form.score_GAD_7, 'GAD_7')
                        }}>
                          {form.score_GAD_7}
                        </span>
                        <span
                          className="view-btn"
                          onClick={() => window.open(form.resultLink, "_blank")}
                          style={{
                            color: '#ff6634',
                            cursor: 'pointer',
                            fontSize: '12px',
                            fontWeight: '600',
                            textDecoration: 'underline',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '3px'
                          }}
                        >
                          <i className="bi bi-eye-fill" style={{ fontSize: '10px' }}></i> View
                        </span>
                      </div>
                    ) : (
                      <span style={{ color: '#6c757d', fontSize: '16px' }}>0</span>
                    )}
                  </td>
                  <td style={{ padding: '15px', verticalAlign: 'middle', textAlign: 'center' }}>
                    {form.score_AUDIT > 0 ? (
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '5px' }}>
                        <span style={{ 
                          fontWeight: '600', 
                          fontSize: '18px', 
                          color: getScoreColor(form.score_AUDIT, 'AUDIT')
                        }}>
                          {form.score_AUDIT}
                        </span>
                        <span
                          className="view-btn"
                          onClick={() => window.open(form.resultLink, "_blank")}
                          style={{
                            color: '#ff6634',
                            cursor: 'pointer',
                            fontSize: '12px',
                            fontWeight: '600',
                            textDecoration: 'underline',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '3px'
                          }}
                        >
                          <i className="bi bi-eye-fill" style={{ fontSize: '10px' }}></i> View
                        </span>
                      </div>
                    ) : (
                      <span style={{ color: '#6c757d', fontSize: '16px' }}>0</span>
                    )}
                  </td>
                  <td style={{ padding: '15px', verticalAlign: 'middle', textAlign: 'center' }}>
                    {form.score_DAST > 0 ? (
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '5px' }}>
                        <span style={{ 
                          fontWeight: '600', 
                          fontSize: '18px', 
                          color: getScoreColor(form.score_DAST, 'DAST')
                        }}>
                          {form.score_DAST}
                        </span>
                        <span
                          className="view-btn"
                          onClick={() => window.open(form.resultLink, "_blank")}
                          style={{
                            color: '#ff6634',
                            cursor: 'pointer',
                            fontSize: '12px',
                            fontWeight: '600',
                            textDecoration: 'underline',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '3px'
                          }}
                        >
                          <i className="bi bi-eye-fill" style={{ fontSize: '10px' }}></i> View
                        </span>
                      </div>
                    ) : (
                      <span style={{ color: '#6c757d', fontSize: '16px' }}>0</span>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Schedule Form Modal */}
      <Modal show={showScheduleModal} onHide={() => setShowScheduleModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Schedule Form Delivery</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted mb-4">Forms will be sent to the client by email.</p>

          {/* Selected Form */}
          <div className="mb-4">
            <h6 className="fw-bold">Selected Form</h6>
            <p className="text-muted">
              {enabledForms.find(form => form.form_id === selectedFormId)?.title_form || 'No form selected'}
            </p>
          </div>

          {/* Delivery Schedule */}
          <div className="mb-4">
            <h6 className="fw-bold mb-3">Delivery Schedule</h6>

            {/* Weekly Option */}
            <div className="mb-3">
              <div
                className={`p-3 border rounded cursor-pointer ${scheduleOption === 'weekly' ? 'border-warning bg-warning bg-opacity-10' : 'border-secondary'}`}
                onClick={() => setScheduleOption(scheduleOption === 'weekly' ? '' : 'weekly')}
                style={{ cursor: 'pointer', transition: 'all 0.2s ease' }}
              >
                <div className="d-flex align-items-center">
                  <div className={`rounded-circle me-3 ${scheduleOption === 'weekly' ? 'bg-warning' : 'bg-light border'}`}
                       style={{ width: '20px', height: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {scheduleOption === 'weekly' && <div className="bg-white rounded-circle" style={{ width: '8px', height: '8px' }}></div>}
                  </div>
                  <div className="flex-grow-1">
                    <div className="fw-bold">Weekly</div>
                    <div className="text-muted small">Send every week</div>
                  </div>
                </div>
              </div>

              {scheduleOption === 'weekly' && (
                <div className="ms-4 mt-3 p-3 bg-light rounded border-start border-warning border-3">
                  <h6 className="fw-bold mb-3">Schedule Details</h6>

                  <div className="mb-3">
                    <label className="form-label fw-bold">Start Date</label>
                    <input
                      type="date"
                      className="form-control"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      min={new Date().toISOString().split('T')[0]}
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label fw-bold">Day of the Week</label>
                    <select
                      className="form-select"
                      value={selectedDayOfWeek}
                      onChange={(e) => setSelectedDayOfWeek(e.target.value)}
                    >
                      <option value="">Select day...</option>
                      <option value="Monday">Monday</option>
                      <option value="Tuesday">Tuesday</option>
                      <option value="Wednesday">Wednesday</option>
                      <option value="Thursday">Thursday</option>
                      <option value="Friday">Friday</option>
                      <option value="Saturday">Saturday</option>
                      <option value="Sunday">Sunday</option>
                    </select>
                  </div>

                  <div className="mb-3">
                    <label className="form-label fw-bold">Time</label>
                    <input
                      type="time"
                      className="form-control"
                      value={selectedTime}
                      onChange={(e) => setSelectedTime(e.target.value)}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Biweekly Option */}
            <div className="mb-3">
              <div
                className={`p-3 border rounded cursor-pointer ${scheduleOption === 'biweekly' ? 'border-warning bg-warning bg-opacity-10' : 'border-secondary'}`}
                onClick={() => setScheduleOption(scheduleOption === 'biweekly' ? '' : 'biweekly')}
                style={{ cursor: 'pointer', transition: 'all 0.2s ease' }}
              >
                <div className="d-flex align-items-center">
                  <div className={`rounded-circle me-3 ${scheduleOption === 'biweekly' ? 'bg-warning' : 'bg-light border'}`}
                       style={{ width: '20px', height: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {scheduleOption === 'biweekly' && <div className="bg-white rounded-circle" style={{ width: '8px', height: '8px' }}></div>}
                  </div>
                  <div className="flex-grow-1">
                    <div className="fw-bold">Biweekly</div>
                    <div className="text-muted small">Send every 2 weeks</div>
                  </div>
                </div>
              </div>

              {scheduleOption === 'biweekly' && (
                <div className="ms-4 mt-3 p-3 bg-light rounded border-start border-warning border-3">
                  <h6 className="fw-bold mb-3">Schedule Details</h6>

                  <div className="mb-3">
                    <label className="form-label fw-bold">Start Date</label>
                    <input
                      type="date"
                      className="form-control"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      min={new Date().toISOString().split('T')[0]}
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label fw-bold">Day of the Week</label>
                    <select
                      className="form-select"
                      value={selectedDayOfWeek}
                      onChange={(e) => setSelectedDayOfWeek(e.target.value)}
                    >
                      <option value="">Select day...</option>
                      <option value="Monday">Monday</option>
                      <option value="Tuesday">Tuesday</option>
                      <option value="Wednesday">Wednesday</option>
                      <option value="Thursday">Thursday</option>
                      <option value="Friday">Friday</option>
                      <option value="Saturday">Saturday</option>
                      <option value="Sunday">Sunday</option>
                    </select>
                  </div>

                  <div className="mb-3">
                    <label className="form-label fw-bold">Time</label>
                    <input
                      type="time"
                      className="form-control"
                      value={selectedTime}
                      onChange={(e) => setSelectedTime(e.target.value)}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Monthly Option */}
            <div className="mb-3">
              <div
                className={`p-3 border rounded cursor-pointer ${scheduleOption === 'monthly' ? 'border-warning bg-warning bg-opacity-10' : 'border-secondary'}`}
                onClick={() => setScheduleOption(scheduleOption === 'monthly' ? '' : 'monthly')}
                style={{ cursor: 'pointer', transition: 'all 0.2s ease' }}
              >
                <div className="d-flex align-items-center">
                  <div className={`rounded-circle me-3 ${scheduleOption === 'monthly' ? 'bg-warning' : 'bg-light border'}`}
                       style={{ width: '20px', height: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {scheduleOption === 'monthly' && <div className="bg-white rounded-circle" style={{ width: '8px', height: '8px' }}></div>}
                  </div>
                  <div className="flex-grow-1">
                    <div className="fw-bold">Monthly</div>
                    <div className="text-muted small">Send every month</div>
                  </div>
                </div>
              </div>

              {scheduleOption === 'monthly' && (
                <div className="ms-4 mt-3 p-3 bg-light rounded border-start border-warning border-3">
                  <h6 className="fw-bold mb-3">Schedule Details</h6>

                  <div className="mb-3">
                    <label className="form-label fw-bold">Start Date</label>
                    <input
                      type="date"
                      className="form-control"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      min={new Date().toISOString().split('T')[0]}
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label fw-bold">Day of the Month</label>
                    <select
                      className="form-select"
                      value={selectedDayOfMonth}
                      onChange={(e) => setSelectedDayOfMonth(e.target.value)}
                    >
                      <option value="">Select day...</option>
                      {Array.from({ length: 31 }, (_, i) => (
                        <option key={i + 1} value={i + 1}>
                          {i + 1}{i === 0 ? 'st' : i === 1 ? 'nd' : i === 2 ? 'rd' : 'th'}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="mb-3">
                    <label className="form-label fw-bold">Time</label>
                    <input
                      type="time"
                      className="form-control"
                      value={selectedTime}
                      onChange={(e) => setSelectedTime(e.target.value)}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Every 6 Months Option */}
            <div className="mb-3">
              <div
                className={`p-3 border rounded cursor-pointer ${scheduleOption === 'sixmonths' ? 'border-warning bg-warning bg-opacity-10' : 'border-secondary'}`}
                onClick={() => setScheduleOption(scheduleOption === 'sixmonths' ? '' : 'sixmonths')}
                style={{ cursor: 'pointer', transition: 'all 0.2s ease' }}
              >
                <div className="d-flex align-items-center">
                  <div className={`rounded-circle me-3 ${scheduleOption === 'sixmonths' ? 'bg-warning' : 'bg-light border'}`}
                       style={{ width: '20px', height: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {scheduleOption === 'sixmonths' && <div className="bg-white rounded-circle" style={{ width: '8px', height: '8px' }}></div>}
                  </div>
                  <div className="flex-grow-1">
                    <div className="fw-bold">Every 6 Months</div>
                    <div className="text-muted small">Send twice a year</div>
                  </div>
                </div>
              </div>

              {scheduleOption === 'sixmonths' && (
                <div className="ms-4 mt-3 p-3 bg-light rounded border-start border-warning border-3">
                  <h6 className="fw-bold mb-3">Schedule Details</h6>

                  <div className="mb-3">
                    <label className="form-label fw-bold">Start Date</label>
                    <input
                      type="date"
                      className="form-control"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      min={new Date().toISOString().split('T')[0]}
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label fw-bold">Day of the Month</label>
                    <select
                      className="form-select"
                      value={selectedDayOfMonth}
                      onChange={(e) => setSelectedDayOfMonth(e.target.value)}
                    >
                      <option value="">Select day...</option>
                      {Array.from({ length: 31 }, (_, i) => (
                        <option key={i + 1} value={i + 1}>
                          {i + 1}{i === 0 ? 'st' : i === 1 ? 'nd' : i === 2 ? 'rd' : 'th'}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="mb-3">
                    <label className="form-label fw-bold">Time</label>
                    <input
                      type="time"
                      className="form-control"
                      value={selectedTime}
                      onChange={(e) => setSelectedTime(e.target.value)}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Send on a Specific Date Option */}
            <div className="mb-3">
              <div
                className={`p-3 border rounded cursor-pointer ${scheduleOption === 'specific' ? 'border-warning bg-warning bg-opacity-10' : 'border-secondary'}`}
                onClick={() => setScheduleOption(scheduleOption === 'specific' ? '' : 'specific')}
                style={{ cursor: 'pointer', transition: 'all 0.2s ease' }}
              >
                <div className="d-flex align-items-center">
                  <div className={`rounded-circle me-3 ${scheduleOption === 'specific' ? 'bg-warning' : 'bg-light border'}`}
                       style={{ width: '20px', height: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {scheduleOption === 'specific' && <div className="bg-white rounded-circle" style={{ width: '8px', height: '8px' }}></div>}
                  </div>
                  <div className="flex-grow-1">
                    <div className="fw-bold">
                      <i className="bi bi-calendar me-2"></i>
                      Send on a Specific Date
                    </div>
                    <div className="text-muted small">Choose a specific date and time</div>
                  </div>
                </div>
              </div>

              {scheduleOption === 'specific' && (
                <div className="ms-4 mt-3 p-3 bg-light rounded border-start border-warning border-3">
                  <h6 className="fw-bold mb-3">Schedule Details</h6>

                  <div className="mb-3">
                    <label className="form-label fw-bold">Date</label>
                    <input
                      type="date"
                      className="form-control"
                      value={selectedDate}
                      onChange={(e) => setSelectedDate(e.target.value)}
                      min={new Date().toISOString().split('T')[0]}
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label fw-bold">Time</label>
                    <input
                      type="time"
                      className="form-control"
                      value={selectedTime}
                      onChange={(e) => setSelectedTime(e.target.value)}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button
            className="btn btn-secondary me-2"
            onClick={() => { setShowScheduleModal(false); resetScheduleForm(); }}
          >
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={handleScheduleFormSubmit}
            disabled={!isScheduleFormValid()}
            style={{ backgroundColor: '#ff6634', borderColor: '#ff6634' }}
          >
            <i className="bi bi-envelope me-2"></i>
            Schedule Form
          </button>
        </Modal.Footer>
      </Modal>

      {/* Confirmation Modal */}
      <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Action</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-center">
            <i className="bi bi-exclamation-triangle text-warning" style={{ fontSize: '48px' }}></i>
            <h5 className="mt-3 mb-3">
              Are you sure you want to {confirmAction} this scheduled form?
            </h5>
            <p className="text-muted">
              This action cannot be undone.
            </p>
          </div>
        </Modal.Body>
        <Modal.Footer className="justify-content-center">
          <button
            className="btn btn-secondary me-2"
            onClick={() => setShowConfirmModal(false)}
            style={{ minWidth: '100px' }}
          >
            Cancel
          </button>
          <button
            className={`btn ${confirmAction === 'cancel' ? 'btn-danger' : 'btn-warning'} ms-2`}
            onClick={handleConfirmAction}
            style={{ minWidth: '100px' }}
          >
            {confirmAction === 'cancel' ? 'Cancel Schedule' : 'Reset Schedule'}
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default JotformSaas;
